/**
 * PhotoCounts pure logic — extracted for unit testing (the AdminPanel
 * pure-helper convention). No React, no network.
 *
 * The pill shows SERVER truth (the same numbers the admin's guests-tab summary
 * computes) plus OPTIMISTIC bumps for sends this device just made. Bump
 * lifecycle (review round 2026-08-01 — age-only pruning both dipped on slow
 * saves and double-counted fast ones):
 *  - addBump at server ACK (the save job is only QUEUED then — the photo is
 *    NOT in the server count yet, so ack age must never expire a bump);
 *  - confirmBump when the save is CONFIRMED done (watchSend / sync path) —
 *    THIS starts the absorb clock: a server count fetched CONFIRM_ABSORB_MS
 *    after confirmation must already include the photo, so the bump drops;
 *  - removeBump when the send ultimately FAILS (the photo never landed);
 *  - a long leak guard (BUMP_MAX_AGE_MS) covers a watcher that dies without
 *    ever confirming or failing — watchSend gives up after ~28min, so 45min
 *    bounds any leak without fighting real slow saves.
 * Known transient: between a fast save's confirmation and the next poll the
 * pill can briefly read +1 over the admin (bump + a fresh scan that already
 * counts the photo). Exact reconciliation would need the server to echo
 * capture ids; accepted as an eventually-consistent display.
 */

// Matches the server's MINIME_COPIES_MAX: the admin counts a photo's copies
// clamped to 7 even if the photographer picked more (CopiesControl allows 9 —
// pre-existing mismatch, flagged separately). The bump must mirror the server.
export const COPIES_MAX = 7;

// Server cache TTL (20s) + margin: a fetch this long after CONFIRMATION
// provably includes the photo.
export const CONFIRM_ABSORB_MS = 25000;

// Leak guard for bumps that never got confirmed/failed (dead watcher) —
// comfortably past watchSend's ~28min give-up.
export const BUMP_MAX_AGE_MS = 45 * 60 * 1000;

/** Add a pending bump at server ACK (idempotent per id — a retry of the same
 * send must not double-count). */
export const addBump = (bumps, id, copies, ts) => {
  const list = bumps || [];
  if (list.some((b) => b.id === id)) return list;
  const clamped = Math.max(1, Math.min(COPIES_MAX, Number(copies) || 1));
  return [...list, { id, ts, photos: 1, copies: clamped }];
};

/** Stamp a bump as confirmed-saved — starts its absorb clock. */
export const confirmBump = (bumps, id, savedAt) =>
  (bumps || []).map((b) => (b.id === id && !b.savedAt ? { ...b, savedAt } : b));

/** Remove a send's bump when it turns out to have failed (no-op if absent). */
export const removeBump = (bumps, id) => (bumps || []).filter((b) => b.id !== id);

/** Drop bumps the fetched server count has provably absorbed (confirmed long
 * enough ago), plus leak-guard expiry for never-resolved bumps. */
export const pruneBumps = (bumps, fetchedAt) =>
  (bumps || []).filter(
    (b) =>
      !(b.savedAt && fetchedAt - b.savedAt > CONFIRM_ABSORB_MS) &&
      fetchedAt - b.ts < BUMP_MAX_AGE_MS
  );

/** server {photos, copies} + pending bumps -> displayed {photos, copies}. */
export const mergeCounts = (server, bumps) => {
  const list = bumps || [];
  return {
    photos: server.photos + list.reduce((n, b) => n + (b.photos || 0), 0),
    copies: server.copies + list.reduce((n, b) => n + (b.copies || 0), 0),
  };
};
